import React, { useState, useEffect } from 'react';
import { Users, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { Button } from '../ui';
import { waitlistService } from '../../services/waitlist';
import type { WaitlistEntry } from '@court-sync/shared';
import toast from 'react-hot-toast';

interface LessonWaitlistSectionProps {
  lessonId: string;
  academyId: string;
  onEntriesChange?: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Waiting',
  notified: 'Notified',
  expired: 'Expired',
  converted: 'Converted',
};

export function LessonWaitlistSection({
  lessonId,
  academyId,
  onEntriesChange,
}: LessonWaitlistSectionProps) {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetchWaitlist = async () => {
    try {
      setLoading(true);
      const res = await waitlistService.getWaitlistForLesson(lessonId, academyId);
      setEntries(res.data || []);
    } catch (err) {
      console.error('Failed to fetch waitlist:', err);
      toast.error('Failed to load waitlist');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) {
      fetchWaitlist();
    }
  }, [expanded, lessonId, academyId]);

  const handleRemove = async (entryId: string) => {
    try {
      setRemovingId(entryId);
      await waitlistService.leaveWaitlist(entryId);
      toast.success('Removed from waitlist');
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
      onEntriesChange?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setRemovingId(null);
    }
  };

  if (entries.length === 0 && !expanded) {
    return null;
  }

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-gray-300"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-1">
          <Users className="h-3.5 w-3.5" />
          Waitlist {entries.length > 0 ? `(${entries.length})` : ''}
        </span>
        {expanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {loading ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              No one on the waitlist
            </div>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {entries.map((entry) => {
                const studentName = entry.customerFirstName && entry.customerLastName
                  ? `${entry.customerFirstName} ${entry.customerLastName}`
                  : 'Student';
                const guardianEmail = entry.customerEmail || '-';
                return (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between gap-2 p-2 rounded bg-gray-50 dark:bg-gray-800 text-xs"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{studentName}</div>
                      <div className="text-gray-500 dark:text-gray-400 truncate">
                        {guardianEmail}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        #{entry.position} • {STATUS_LABELS[entry.status] || entry.status}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                      onClick={() => handleRemove(entry.id)}
                      disabled={removingId === entry.id}
                      aria-label={`Remove ${studentName} from waitlist`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
